import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import {useTheme} from '@mui/material/styles';
import {SnackbarField} from '@/components/common';
import CompanyGLCategoryOpera from '@/pages/components/manage/category/co-gl-cate/opera/CompanyGLCategoryOpera';
import CompanyGLEditorPreview from '@/pages/components/manage/category/co-gl-cate/opera/CompanyGLEditorPreview';
import * as CompanyService from '@/services/accountService/companyService';
import {usePageCrud} from '@/hooks/usePageCrud';

/**
 * Company GL Category 页面（审计模块）。
 * 结构参照 {@link AuditCompanyFSCategoryList}：
 *  - 列出公司，每行提供 Operation / Preview 按钮
 *  - Operation 弹窗内对该公司的 `account_company_gl_category` 进行 CRUD + 查看变更历史
 *  - Preview 弹窗只读展示当前别名映射
 *
 * 关联需求：docs/account-company-gl-category-requirements.md §6.2.4
 */
const CompanyGLCategoryList: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pageNum, setPageNum] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(25);
    const [total, setTotal] = useState<number>(0);
    const [companies, setCompanies] = useState<CompanyService.CompanyInfo[]>([]);

    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message?: string;
        severity?: 'success' | 'error'
    }>({open: false});

    const [operaOpen, setOperaOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<{ id: number; name?: string } | null>(null);
    const {canRead, canUpdate} = usePageCrud({
        read: ['co_gl_cate_list', 'co_gl_cate_list_by_company', 'co_gl_cate_get'],
        create: 'account:co_gl_cate:create',
        update: 'account:co_gl_cate:update',
        remove: 'account:co_gl_cate:delete',
    });

    useEffect(() => {
        const run = async () => {
            setLoading(true);
            try {
                const resp = await CompanyService.pageCompanies({pageNumber: pageNum, pageSize} as any);
                const arr: CompanyService.CompanyInfo[] = (resp as any)?.list || [];
                setCompanies(arr);
                setTotal(Number((resp as any)?.totalRow || 0));
                setError(null);
            } catch (e: any) {
                setError(e?.message || 'Load failed');
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [pageNum, pageSize]);

    const tableRows = useMemo(() => {
        return (companies || []).map(c => ({
            id: Number(c.companyId),
            companyName: c.companyName,
            companyChineseName: c.companyChineseName,
            industry: c.industryName || c.industry,
        }));
    }, [companies]);

    const openOpera = (row: { id: number; companyName?: string; companyChineseName?: string }) => {
        setSelectedCompany({id: Number(row.id), name: row.companyName || row.companyChineseName});
        setOperaOpen(true);
    };
    const openPreview = (row: { id: number; companyName?: string; companyChineseName?: string }) => {
        setSelectedCompany({id: Number(row.id), name: row.companyName || row.companyChineseName});
        setPreviewOpen(true);
    };

    return (
        <Box sx={{p: '20px'}}>
            <Box sx={{
                height: 'calc(100vh - 120px)',
                minHeight: '70vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexShrink={0}>
                    <Typography variant="h6" fontWeight={700}>Company GL Category</Typography>
                    <span/>
                </Stack>
                {loading && <CircularProgress sx={{display: 'block', mx: 'auto', my: 2, flexShrink: 0}}/>}
                {error && <Box sx={{color: 'error.main', px: 1, py: 0.5, fontSize: 12, flexShrink: 0}}>{error}</Box>}
                <TableContainer
                    component={Paper}
                    sx={{flex: 1, minHeight: 0, overflow: 'auto'}}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 700}} align="center">ID</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Company Name</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Company Chinese Name</TableCell>
                                <TableCell sx={{fontWeight: 700}} align="center">Industry</TableCell>
                                <TableCell sx={{fontWeight: 700}} align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableRows.length === 0 && (
                                <TableRow><TableCell colSpan={5} align="center">No data</TableCell></TableRow>
                            )}
                            {tableRows.map((r) => (
                                <TableRow key={String(r.id)} hover>
                                    <TableCell align="center">{r.id}</TableCell>
                                    <TableCell>{r.companyName}</TableCell>
                                    <TableCell>{r.companyChineseName}</TableCell>
                                    <TableCell align="center">{r.industry ?? '-'}</TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            {canUpdate && (
                                            <Button size="small" variant="outlined"
                                                    sx={{textTransform: 'none', fontWeight: 600}}
                                                    onClick={() => openOpera(r)}>
                                                Operation
                                            </Button>
                                            )}
                                            {canRead && (
                                            <Button size="small" variant="outlined"
                                                    sx={{textTransform: 'none', fontWeight: 600}}
                                                    onClick={() => openPreview(r)}>
                                                Preview
                                            </Button>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={Math.max(0, Math.min(Math.ceil(total / pageSize) - 1, pageNum - 1))}
                    onPageChange={(_, newPage) => setPageNum(newPage + 1)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={(e) => {
                        const next = parseInt(e.target.value, 10) || 25;
                        setPageSize(next);
                        setPageNum(1);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{mt: 1, flexShrink: 0}}
                />
            </Box>

            <Dialog open={operaOpen} onClose={() => setOperaOpen(false)} fullWidth maxWidth="lg">
                <DialogTitle>Operation Company GL Category
                    - {selectedCompany?.name ?? selectedCompany?.id}</DialogTitle>
                <DialogContent dividers>
                    {selectedCompany && (
                        <CompanyGLCategoryOpera
                            companyId={selectedCompany.id}
                            companyName={selectedCompany.name}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="lg">
                <DialogTitle>Preview Company GL Category - {selectedCompany?.name ?? selectedCompany?.id}</DialogTitle>
                <DialogContent dividers>
                    {selectedCompany && (
                        <CompanyGLEditorPreview
                            companyId={selectedCompany.id}
                            companyName={selectedCompany.name}
                        />
                    )}
                </DialogContent>
            </Dialog>
            <SnackbarField open={!!snackbar.open} message={snackbar.message || ''} severity={snackbar.severity}
                           onClose={() => setSnackbar(s => ({...s, open: false}))}/>
        </Box>
    );
};

export default CompanyGLCategoryList;
